import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useNavigate, useParams } from 'react-router-dom';
import PageHeader from '../../../components/common/PageHeader/PageHeader';
import SmallDetailBox from '../../../components/common/DetailBox/SmallDetailBox';
import LargeDetailBox from '../../../components/common/DetailBox/LargeDetailBox';
import '../CustomerDetail.css';
import '../UserDetail.css';
import { baseurl } from '../../../api';
import authenticatedAxios from '../../../authenticatedAxios';

import { isRequired, isTextOnly, isEmail, maxLength, isNumberOnly } from '../../../validation';
import InputBoxLarge from '../../../components/common/InputBox/InputBoxLarge';
import InputBoxSmall from '../../../components/common/InputBox/InputBoxSmall';
import SearchSelectBoxDrown from '../../../components/common/SelectBox/searchSelect';
import { showAlert } from '../../../components/common/ExportLabel/Exporterror';
import ArchivePopup from '../../../components/Popup/ArchivePopup/ArchivePopup';
import { fetchTimezone } from '../../../services/Localization/CountryService';
import PhoneInputSelect from '../../../components/common/SelectBox/PhoneInputSelect';
import CloseButton from '../../../components/common/CloseButton/CloseButton';

const UserDetailPage = () => {
    const [userDetail, setUserDetail] = useState(null);
    const [editedFields, setEditedFields] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isEdit, setIsEdit] = useState(false);
    const [showOptions, setShowOptions] = useState(false);
    const [isArchivePopupOpen, setIsArchivePopupOpen] = useState(false);
    const [userRoles, setUserRoles] = useState([]);
    const [languages, setLanguages] = useState([]);
    const [countries, setCountries] = useState([]);
    const [timeZone, setTimezone] = useState([]);
    const [profilePicFile, setProfilePicFile] = useState(null);
    const [profilePicPreview, setProfilePicPreview] = useState(null);

    // manual
    const [isManualResetOpen, setIsManualResetOpen] = useState(false);
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [popupErrorMessage, setPopupErrorMessage] = useState('');

    const navigate = useNavigate();
    const params = useParams();
    const id = params.id;
    const type = params.type || 'organizational';

    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                const endpoint = type === 'internal' 
                    ? `${baseurl}accounts/users/internal/`
                    : `${baseurl}accounts/users/organizational/`;

                const [userResponse, rolesResponse, languagesResponse, countriesResponse] = await Promise.all([
                    authenticatedAxios.get(endpoint, { params: { id } }),
                    authenticatedAxios.get(`${baseurl}accounts/roles/internal/active/`),
                    authenticatedAxios.get(`${baseurl}localization/languages/active/`),
                    authenticatedAxios.get(`${baseurl}localization/country/active/`)
                ]);

                if (userResponse.data.response_code === 1) {
                    setUserDetail(userResponse.data.data);
                }

                setUserRoles(rolesResponse.data.data || []);
                setLanguages(languagesResponse.data.data || []);
                setCountries(countriesResponse.data.data || []);
                setLoading(false);
            } catch (error) {
                setError('Failed to fetch data');
                setLoading(false);
            }
        };

        if (id) {
            fetchInitialData();
        }
    }, [id, type]);

    useEffect(() => {
        async function loadTimezone() {
          const data = await fetchTimezone();
          setTimezone(data);
        }
    
        loadTimezone();
    }, []);

    const handleEditClick = () => {
        if (type !== 'internal') {
            navigate(`/user-accounts/edit/${id}`);
        } else {
            setIsEdit(true);
        }
    };

    const handleProfilePicChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            setProfilePicFile(file);
            setProfilePicPreview(URL.createObjectURL(file));
        }
    };

    const handleEditProfilePic = () => {
        document.getElementById('profile-pic-input').click();
    };

    const handleArchive = async () => {
        try {
          const response = await authenticatedAxios.get(
            `${baseurl}accounts/users/archive/?id=${userDetail?.id}`
          );
      
          if (response.status >= 200 && response.status < 300) {
            const userLevel = userDetail?.user_level;
      
            if (userLevel === 'Internal User') {
              navigate('/internal-users');
            } else {
              navigate('/user-accounts');
            }
          }
        } catch (error) {
          // Optional: handle errors here
        }
    };

    const handleUpdate = async () => {
        try {
            const formData = new FormData();

            if (profilePicFile) {
                formData.append('profile_pic', profilePicFile);
            }

            for (const key in editedFields) {
                if (key !== 'profile_pic') {
                    formData.append(key, editedFields[key]);
                }
            }

            const endpoint = type === 'internal' 
                ? `${baseurl}accounts/users/internal/?id=${id}`
                : `${baseurl}accounts/users/organizational/?id=${id}`;

            const user_response = await authenticatedAxios.put(endpoint, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            const code = parseInt(user_response.data.response_code);

            if (code === 1) {
                const response = await authenticatedAxios.get(endpoint);
                setUserDetail(response.data.data);
                setEditedFields({});
                setIsEdit(false);
                setProfilePicPreview(null);
                setError(null);
            } else {
                showAlert(user_response.data.message || 'Failed to update user account.');
            }
        } catch (err) {
            console.error("Update failed", err);
            setError('Failed to update user details');
        }
    };

    const toggleOptions = () => {
        setShowOptions(prev => !prev);
    };

    if (loading) return <div>Loading...</div>;
    if (error) return <div>Error: {error}</div>;

    const pageHeaderProps = type === 'internal'
        ? {
            pageName: 'Internal Users /',
            nestedpageName: userDetail?.full_name,
            linkTo: '/internal-users',
        }
        : {
            pageName: 'User Accounts /',
            nestedpageName: userDetail?.full_name,
            linkTo: '/user-accounts',
        };

    const handleEmailReset = async () => {
        const toastId = toast.loading('Sending reset email...');

        try {
            const response = await authenticatedAxios.post(`${baseurl}accounts/send-reset-email/`, {
            email: userDetail.email,
            id: userDetail.id,
            });

            if (response.data.response_code === 1) {
            toast.update(toastId, {
                render: 'Reset email sent successfully!',
                type: 'success',
                isLoading: false,
                autoClose: 3000,
            });
            } else {
            toast.update(toastId, {
                render: response.data.message || 'Something went wrong!',
                type: 'error',
                isLoading: false,
                autoClose: 3000,
            });
            }
        } catch (error) {
            toast.update(toastId, {
            render: 'Failed to send reset email. Please try again.',
            type: 'error',
            isLoading: false,
            autoClose: 3000,
            });
        }
    };

    const handleManualReset = async () => {
        if (!newPassword || !confirmPassword) {
          setPopupErrorMessage("Both fields are required.");
          return;
        }
        if (newPassword !== confirmPassword) {
          setPopupErrorMessage("Passwords do not match.");
          return;
        }
      
        const toastId = toast.loading('Resetting password...');
        try {
          const response = await authenticatedAxios.post(
            `${baseurl}accounts/reset-password-manual/${userDetail?.id}/`,
            { new_password: newPassword, confirm_password: confirmPassword }
          );
      
          toast.update(toastId, {
            render: response.data.message || 'Password reset successful!',
            type: 'success',
            isLoading: false,
            autoClose: 3000,
          });
      
          setIsManualResetOpen(false);
          setNewPassword('');
          setConfirmPassword('');
          setPopupErrorMessage('');
        } catch (error) {
          toast.update(toastId, {
            render: error.response?.data?.message || 'Password reset failed.',
            type: 'error',
            isLoading: false,
            autoClose: 3000,
          });
        }
    };

    const handleInputChange = (field, value) => {
        setUserDetail(prev => ({
            ...prev,
            [field]: value,
        }));
        
        setEditedFields(prev => ({
            ...prev,
            [field]: value,
        }));
    };

    return (
        <div className="whole-container">
            <div className="top-heading">
                <PageHeader {...pageHeaderProps} />
            </div>

            <div className="User-detail-container">
                <div className="my-profile-whole left-user-details-whole">
                    <div className="my-profile-top company-left-first left-customer">
                        <div className="company-img-name-container">
                            <div className="company-img-container">
                                {!isEdit ? (
                                    userDetail?.profile_pic ? (
                                        <img
                                            src={`${baseurl.replace(/\/$/, '')}/${userDetail.profile_pic.replace(/^\//, '')}`}
                                            alt="Profile"
                                            className="comp-img user-image"
                                        />
                                    ) : (
                                        <img
                                            src="/assets/avatar.jpg"
                                            alt="Avatar"
                                            className="comp-img user-image avatar-img"
                                        />
                                    )
                                ) : (
                                    <>
                                        <img
                                            src={
                                                profilePicPreview || 
                                                (userDetail?.profile_pic 
                                                  ? `${baseurl.replace(/\/$/, '')}/${userDetail.profile_pic.replace(/^\//, '')}` 
                                                  : '/assets/avatar.jpg')
                                            }  
                                            alt="Profile"
                                            className="comp-img user-image"
                                        />
                                        <img
                                            src="/assets/company_edit.png"
                                            alt="edit"
                                            className="edit-company"
                                            onClick={handleEditProfilePic}
                                        />
                                        <input
                                            id="profile-pic-input"
                                            type="file"
                                            accept="image/*"
                                            style={{ display: 'none' }}
                                            onChange={handleProfilePicChange}
                                        />
                                    </>
                                )}
                            </div>

                            <div>
                                <span className="common-text fts-17 dk-gray">{userDetail?.full_name || 'N/A'}</span>
                                <span className="common-text fts-13 dk-gray txt-lc">{userDetail?.email || 'N/A'}</span>
                            </div>
                        </div>
                        <div className="company-edit-button">
                            <button className="fts-15 black" onClick={isEdit ? handleUpdate : handleEditClick}>
                                {isEdit ? 'Save' : 'Edit'}
                            </button>
                        </div>
                    </div>

                    {!isEdit ? (
                        <div className="company-left-second left-customer">
                            {type !== 'internal' && (
                            <LargeDetailBox label="Company Name" value={userDetail?.organization?.name || 'N/A'} />
                            )}
                            <LargeDetailBox label="User Name" value={userDetail?.full_name || 'N/A'} />
                            <SmallDetailBox label="Email" value={userDetail?.email || 'N/A'} />
                            <SmallDetailBox 
                            label="Mobile Number" 
                            value={`${userDetail?.country_code?.country_code || 'N/A'} ${userDetail?.phone || 'N/A'}`} 
                            />
                            {type !== 'internal' && (
                                <SmallDetailBox
                                    label="User Type"
                                    value={userDetail?.user_type || 'N/A'}
                                />
                            )}

                            <SmallDetailBox label="User Role" value={userDetail?.role?.name || 'N/A'} />
                            <SmallDetailBox label="Preferred Language" value={userDetail?.preferred_language?.name || 'N/A'} />
                            <SmallDetailBox 
                                label="Timezone" 
                                value={
                                        userDetail?.timezone?.name && userDetail?.timezone?.timezone
                                            ? userDetail.timezone.name + ' ' + userDetail.timezone.timezone
                                            : 'N/A'
                                        }
                            />
                            {type === 'internal' ? (
                                <SmallDetailBox
                                    label="Last Active Date and Time"
                                    value={
                                        userDetail?.last_active
                                        ? new Date(userDetail.last_active).toLocaleString()
                                        : 'N/A'
                                    }
                                />
                            ) : (
                                <LargeDetailBox
                                    label="Last Active Date and Time"
                                    value={
                                        userDetail?.last_active
                                        ? new Date(userDetail.last_active).toLocaleString()
                                        : 'N/A'
                                    }
                                />
                            )}
                        </div>
                        ) : (
                        <div className="left-customer ">
                            <InputBoxLarge
                                label="Name"
                                border='blue-border'
                                defaultValue={userDetail?.full_name}
                                onChange={(e) => handleInputChange("full_name", e.target.value)}
                                validators={[isRequired, isTextOnly]}
                            />
                            <div className='user-edit-box'>
                                <InputBoxSmall
                                    label="Email"
                                    className='w-50'
                                    border='blue-border'
                                    defaultValue={userDetail?.email}
                                    onChange={(e) => handleInputChange("email", e.target.value)}
                                    validators={[isRequired, isEmail]}
                                />

                                <PhoneInputSelect
                                    label="Mobile Number"
                                    className='w-50'
                                    options={countries.map(c => ({
                                        value: c.id.toString(),
                                        label: c.country_code
                                    }))}
                                    border='blue-border'
                                    onChange={(e) => handleInputChange("phone", e.target.value)}
                                    onSelectChange={(e) => handleInputChange("country_code", e.target.value)}
                                    defaultValue={userDetail?.phone}
                                    defaultOption={userDetail?.country_code?.id} 
                                    validators={[maxLength(14), isNumberOnly]}
                                />
                            </div>
                            <div className='user-edit-box'>
                                
                            {type !== 'internal' && (
                                <SearchSelectBoxDrown
                                label="User Type"
                                className='w-50'
                                border='blue-border'
                                options={[
                                    { value: 'Vansaler', label: 'Vansaler' },
                                    { value: 'Employee', label: 'Employee' },
                                    { value: 'Both', label: 'Both' }
                                ]}
                                defaultValue={userDetail?.user_type}
                                onChange={(e) => setUserDetail({ ...userDetail, user_type: e.target.value })}
                                />
                            )}
                                <SearchSelectBoxDrown
                                    label="User Timezone"
                                    options={timeZone}
                                    defaultValue={userDetail?.timezone?.name && userDetail?.timezone?.timezone
                                            ? userDetail.timezone.name + ' ' + userDetail.timezone.timezone
                                            : 'N/A'}
                                    onChange={(e) => handleInputChange("timezone", e.target.value)}
                                    border='blue-border'
                                    className="w-50"
                                />                                   
                            </div>

                            <div className='user-edit-box'>
                                <SearchSelectBoxDrown
                                    label="User Role"
                                    className="w-50"
                                    border='blue-border'
                                    options={userRoles.map(role => ({
                                    value: role.id,
                                    label: role.name
                                    }))}
                                    defaultValue={userDetail?.role?.id}
                                    onChange={(e) => setUserDetail({ 
                                    ...userDetail, 
                                    role: userRoles.find(role => role.id === parseInt(e.target.value)) 
                                })}
                            />
                                <SearchSelectBoxDrown
                                    label="Preferred Language"
                                    border='blue-border'
                                    options={languages.map(lang => ({
                                    value: lang.id,
                                    label: lang.name
                                    }))}
                                    className="w-50"
                                    defaultValue={userDetail?.preferred_language?.id || userDetail?.preferred_language}
                                    onChange={(e) => handleInputChange("preferred_language", e.target.value)}
                                />
                            </div>
                        </div>
                    )}
                    
                    {/* manual reset password popup */}
                    {isManualResetOpen && (
                        <div className="popup-overlay manual-reset-container">
                            <div className="popup popup1">
                            <span className="common-text fts-20 ftw-5">Reset Password</span>
                            <CloseButton onClick={() => setIsManualResetOpen(false)} />

                            {popupErrorMessage && (
                                <div className="popup-error-message fts-13" style={{ color: 'red', marginBottom: '10px' }}>
                                {popupErrorMessage}
                                </div>
                            )}

                            <div className="user-edit-box ">
                                <InputBoxSmall
                                label="New Password"
                                className="w-50"
                                border="blue-border"
                                type="password"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                />
                                <InputBoxSmall
                                label="Confirm Password"
                                className="w-50"
                                border="blue-border"
                                type="password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                />
                            </div>

                            <div className="popup-actions reset-popup-buttons">
                                <button className="save-btn yes-button" onClick={handleManualReset}>Save</button>
                                <button className="cancel-btn no-button" onClick={() => setIsManualResetOpen(false)}>Cancel</button>
                            </div>
                            </div>
                        </div>
                    )}

                    <div className="user-detail-button-container">
                        <button
                            className="archive-user white common-text fts-15 ftw-4"
                            onClick={() => setIsArchivePopupOpen(true)}
                        >
                            <svg
                            className="icon"
                            xmlns="http://www.w3.org/2000/svg"
                            width="18"
                            height="18"
                            viewBox="0 0 18 18"
                            fill="none"
                            >
                            <path
                                d="M9 15L13 11L11.6 9.6L10 11.2V7H8V11.2L6.4 9.6L5 11L9 15ZM0 18V2.8L2.3 0H15.7L18 2.8V18H0ZM2.4 3H15.6L14.75 2H3.25L2.4 3Z"
                                fill="white"
                            />
                            </svg>
                            {userDetail?.is_archived ? 'Unarchive User' : 'Archive User'}
                        </button>
                       
                        <div className="reset-password-container">
                            <span className="reset-password dk-gray common-text fts-15 ftw-4" onClick={toggleOptions}>
                                Reset Password <span className={`reset-password-icon down-icon ${showOptions ? 'rotate' : ''}`}>▼</span>
                            </span>
                            {showOptions && (
                                <div className="reset-options">
                                    <button 
                                    className="common-text fts-15 ftw-4"
                                    onClick={() => setIsManualResetOpen(true)}>Manual
                                    </button>
                                    <button 
                                        className="common-text by-email fts-15 ftw-4"
                                        onClick={handleEmailReset}
                                    >
                                        By Email
                                    </button>
                                </div>
                            )}
                        </div>
                    </div> 
                </div>
            </div>
            <ArchivePopup
                isOpen={isArchivePopupOpen}
                isArchived={userDetail?.is_archived}
                archivename={userDetail?.full_name}
                onSave={handleArchive}
                onClose={() => setIsArchivePopupOpen(false)}
            />
        </div>
    );
};

export default UserDetailPage;
